<template>
  <main id="invest" class="invest-container">
    <Invest-SectionsEntry id="entry" />
    <div class="invest-content">
      <Invest-SectionsCities
        id="cities"
        :loading="loadingCities"
        :citiesList="data.analysis"
        :totalCitiesCount="data.analysisCount"
        :currentPage="page"
        :sort="sort"
      />
      <lazy-sections-blog-articles
        v-if="articles"
        id="news-section"
        topCtaLink="https://horiz.io/investissement-immobilier/villes-ou-investir"
        :content="articles"
        :title="`<strong>Nos conseils</strong> sur les villes où investir`"
      />
      <lazy-sections-community-ad />
    </div>
  </main>
</template>

<script setup>
import { currentYear } from '@/helpers/currentYear'

const config = useRuntimeConfig()
const investApi = useInvestApi()
const cmsApi = useCmsApi()
const route = useRoute()

const page = computed(() => parseInt(route?.params?.page ?? 1))
const sort = computed(() => route?.query?.sort ?? 'pertinence')

const {
  pending: loadingCities,
  data,
  refresh,
} = await useLazyAsyncData(
  `cities-list-page-${page.value}-${sort.value}`,
  () => investApi.getCitiesList({ skip: (page.value - 1) * 20, limit: 20, sort: sort.value }),
  {
    default: () => ({ analysis: [], analysisCount: 0 }),
  },
)

const { data: articles } = await useLazyAsyncData(
  'where-to-invest-articles',
  () => cmsApi.getPageArticleOrChildCategoryBySlug('investissement-immobilier', 'villes-ou-investir'),
  {
    default: () => ({ articles: null }),
  },
)

watch(
  () => route.query,
  () => refresh(),
)

onMounted(() => {
  const tracking = useTracking()
  tracking.trackAfterMounted('Page accueil villes loaded')
})

// Metas
const title = `Où investir par ville ? Investissement Locatif en ${currentYear} | Horiz.io`

useMeta({
  title,
  description:
    'Retrouvez toutes nos analyses, chiffres et conseils. Assurez-vous de faire un investissement locatif rentable dans la ville de votre choix en France.',
  image: 'Horizio-ou-investir-immobilier.jpg',
  imageAlt: "Horiz.io, pilotez votre gestion locative en ligne'",
  url: `${config.public.hpBaseUrl}/ou-investir`,
  appleItunesApp: 'app-id=1596721394',
})

useHead({
  title,
  link: [
    {
      rel: 'canonical',
      href: `${config.public.hpBaseUrl}/ou-investir`,
    },
  ],
})

useJsonld([
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Horiz',
    alternateName: 'Rendement Locatif',
    url: `${config.public.hpBaseUrl}/ou-investir`,
    logo: `${config.public.hpBaseUrl}/imgs/logo.png`,
  },
  {
    '@context': 'https://schema.org/',
    '@type': 'WebPage',
    name: 'Horiz',
    speakable: {
      '@type': 'SpeakableSpecification',
      xpath: ['/html/head/title', "/html/head/meta[@name='description']/@content"],
    },
    url: `${config.public.hpBaseUrl}/ou-investir`,
  },
  {
    '@context': 'http://www.schema.org',
    '@type': 'Product',
    name: 'Horiz',
    brand: ' Horiz',
    image: `${config.public.hpBaseUrl}/imgs/logo.png`,
    description: "/html/head/meta[@name='description']/@content",
    aggregateRating: {
      '@type': 'aggregateRating',
      ratingValue: '5',
      reviewCount: '62',
      bestRating: '5 ',
    },
  },
  {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: config.public.hpBaseUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Ou investir ?',
        item: `${config.public.hpBaseUrl}/ou-investir`,
      },
    ],
  },
])
</script>

<style lang="less" scoped>
.invest-container {
  .invest-content {
    min-height: 100%;
    margin: auto;
    padding-bottom: 35px;
  }

  #cities {
    .app-padding();
    padding-bottom: 50px;
    max-width: @bp-desktop-xl-max-width;
    margin: 0 auto;
  }

  #news-section {
    background-color: var(--ds-color-primary-25);
    padding: 32px 0px;

    @media @bp-desktop {
      :deep(.container) {
        padding: 0 50px;
        max-width: @bp-desktop-xl-max-width;
        margin: 0 auto;

        .title-container {
          max-width: inherit;
        }

        .content-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          max-width: inherit;
          width: 100%;
        }
      }
    }
  }
  :deep(.news) {
    @media @bp-desktop {
      margin: auto;
    }
  }
}
</style>
